import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { humanize } from '@/lib/format';
import { useBusinessUnits } from '@/lib/reference';

/**
 * Business unit is a **set**, not a value: 14 of the 76 real opportunities
 * carry lines from two or three business units (§5.1 [R2]). Rendering it as a
 * single value would silently pick one and hide the rest, which is why the
 * column is called "BU mix" everywhere it appears.
 */
export function BusinessUnitChips({ units }: { units: unknown }): React.ReactElement {
  const label = useBusinessUnits().label;
  const list = Array.isArray(units) ? (units as string[]) : [];
  if (list.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map((unit) => (
        <Badge key={unit} variant="outline">
          {label(unit)}
        </Badge>
      ))}
    </span>
  );
}

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'danger' | 'info' | 'outline'> = {
  open: 'info',
  won: 'success',
  lost: 'danger',
  no_decision: 'outline',
  on_hold: 'outline',
  design_win_lapsed: 'danger',
  invalid: 'outline',
};

/** §9: the word is the signal; the colour is emphasis. */
export function StatusBadge({ status }: { status: string }): React.ReactElement {
  return <Badge variant={STATUS_VARIANT[status] ?? 'default'}>{humanize(status)}</Badge>;
}

export function RealizationBadge({ status }: { status: string }): React.ReactElement | null {
  if (status === 'not_applicable') return null;
  return <Badge variant="success">Realization: {humanize(status)}</Badge>;
}

export function OpportunityLink({ id, label }: { id: string; label: string }): React.ReactElement {
  return (
    <Link to={`/opportunities/${id}`} className="font-medium hover:underline">
      {label}
    </Link>
  );
}
